import type { QueryClient } from "@tanstack/react-query";
import { apiFetch, inspectSession } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { beginPrivateSessionChange, finishPrivateSessionChange, getPrivateSessionState } from "@/lib/api/private-session";
import { restaurantSessionChannel, resetRestaurantPrivateState } from "@/shared/sessionChannel";
import { firebaseSessionAdapter } from "@/lib/firebase/auth";

export async function changePrivateSession<T>(client: QueryClient, operation: () => Promise<T>): Promise<T> {
  beginPrivateSessionChange();
  const generation = getPrivateSessionState().generation;
  const assertOwnedChange = () => {
    const current = getPrivateSessionState();
    if (current.generation !== generation || !current.changing) throw new DOMException("Session change superseded", "AbortError");
  };
  const end = restaurantSessionChannel.begin();
  try {
    await resetRestaurantPrivateState(client, generation);
    assertOwnedChange();
    await firebaseSessionAdapter.signOut().catch(() => undefined);
    assertOwnedChange();
    const result = await operation();
    assertOwnedChange();
    return result;
  } finally {
    try {
      if (getPrivateSessionState().generation === generation) {
        const session = await inspectSession();
        if (getPrivateSessionState().generation === generation) {
          await resetRestaurantPrivateState(client, generation);
          if (getPrivateSessionState().generation === generation) {
            finishPrivateSessionChange(session?.seller.storeSlug, session?.seller.id);
            if (session) client.setQueryData(["session"], session);
          }
        }
      }
    } catch { /* Unknown binding remains paused; channel recovery is a session read, never a write retry. */ }
    end();
    if (getPrivateSessionState().changing) restaurantSessionChannel.recover();
  }
}
export function switchPrivateStore(client: QueryClient, storeSlug: string) {
  return changePrivateSession(client, () => apiFetch(endpoints.seller.switchStore, { method: "POST", body: { storeSlug } }));
}
